import React, { forwardRef } from 'react';
import classNames from 'classnames';

import { Comp, useId } from '@redsift/design-system';

import { ChartSize, ColorTheme, LinearData, TooltipVariant } from '../../types';
import { BarChartLegendVariant, BarChartProps } from './types';
import { EmptyBarChart } from './EmptyBarChart';
import { LoadingBarChart } from './LoadingBarChart';
import { RenderedOrdinalBarChart } from './RenderedOrdinalBarChart';
import { RenderedLinearBarChart } from './RenderedLinearBarChart';
import { BarOrientation } from '../Bar';
import { isValidDate } from '../../utils';
import { BarChartSection } from './BarChartSection';
import { BarChartBars, BarChartGroupedTooltip } from './BarChartBars';
import { Axis } from '../Axis';
import { Legend } from '../Legend';
import { LegendItem } from '../LegendItem';
import { ChartContainerDescription, ChartContainerTitle } from '../ChartContainer';
import { BarChartOverlay } from './BarChartOverlay';

const COMPONENT_NAME = 'BarChart';
const CLASSNAME = 'redsift-barchart';

/**
 * BarChart visualizes categorical data with rectangular bars.
 * Built on D3.js with responsive sizing and interactive tooltips.
 *
 * This is the only public entry point for bar charts. Do not import
 * `RenderedLinearBarChart`, `RenderedOrdinalBarChart`, `EmptyBarChart`, or
 * `LoadingBarChart` directly — `BarChart` selects the correct renderer
 * (linear vs. ordinal scale) automatically based on the data type,
 * and manages all loading and empty states internally.
 *
 * Supports horizontal/vertical orientation, stacked/grouped bars,
 * and various legend placements.
 *
 * Data format: `CategoryData` `[{ key: string, value: number }]`
 *
 * @example
 * // Basic horizontal bar chart with CategoryData
 * const data = [
 *   { key: 'Product A', value: 120 },
 *   { key: 'Product B', value: 85 },
 *   { key: 'Product C', value: 200 },
 * ];
 * <BarChart data={data} size="medium" />
 *
 * @example
 * // Vertical bar chart with tooltips and click handler
 * <BarChart
 *   data={salesData}
 *   orientation="vertical"
 *   tooltipVariant="value"
 *   onBarClick={(d) => handleBarClick(d.key)}
 * />
 *
 * @example
 * // Semantic colorTheme (maps keys to status colors)
 * <BarChart
 *   data={[
 *     { key: 'Passed', value: 45 },
 *     { key: 'Warning', value: 12 },
 *     { key: 'Failed', value: 8 },
 *   ]}
 *   colorTheme={{
 *     success: 'Passed',
 *     warning: 'Warning',
 *     danger: 'Failed',
 *   }}
 * />
 *
 * @example
 * // Grouped bar chart with legend
 * <BarChart
 *   data={groupedData}
 *   dataType="TwoCategoryData"
 *   legendVariant="externalLabel"
 *   colorTheme="dark"
 * />
 *
 * @example
 * // With custom label formatter and capping
 * <BarChart
 *   data={percentages}
 *   labelDecorator={(value) => `${value}%`}
 *   caping={10}
 *   others={true}
 * />
 */

export const BaseBarChart: Comp<BarChartProps, HTMLDivElement> = forwardRef((props, ref) => {
  const {
    barProps,
    barRole,
    caping,
    chartRef,
    className,
    colorTheme = ColorTheme.default,
    data: propsData,
    dataType: propsDataType,
    emptyComponent,
    id: propsId,
    isBarSelected = () => true,
    labelDecorator,
    legendDecorator,
    legendProps,
    legendVariant: propsLegendVariant,
    localeText = { emptyChartText: 'No Data' },
    onBarClick,
    orientation = BarOrientation.horizontal,
    others = true,
    size = ChartSize.medium,
    sortingMethod = 'desc-value',
    theme,
    tooltipDecorator,
    tooltipVariant = TooltipVariant.value,
    xAxisPlacement = 'bottom',
    xAxisVariant,
    yAxisPlacement = 'left',
    yAxisVariant,
    ...forwardedProps
  } = props;
  const [_id] = useId();
  const id = propsId ?? _id;

  const legendVariant = propsLegendVariant ?? BarChartLegendVariant.externalLabel;

  if (propsData === undefined || propsData === null) {
    return <LoadingBarChart id={id} {...forwardedProps} ref={ref} />;
  }

  if (propsData.length === 0) {
    return (
      <EmptyBarChart
        data={propsData}
        emptyComponent={emptyComponent}
        id={id}
        localeText={localeText}
        size={size}
        {...forwardedProps}
        ref={ref}
      />
    );
  }

  const dataType = propsDataType
    ? propsDataType
    : Array.isArray(propsData[0].key)
    ? 'TwoCategoryData'
    : propsData[0].key instanceof Date
    ? 'LinearData'
    : typeof propsData[0].key === 'number'
    ? 'LinearData'
    : typeof propsData[0].key === 'string' && isValidDate(propsData[0].key)
    ? 'LinearData'
    : typeof propsData[0].key === 'string'
    ? 'CategoryData'
    : 'CategoryData';

  if (dataType === 'CategoryData') {
    return (
      <RenderedOrdinalBarChart
        barProps={barProps}
        barRole={barRole}
        caping={caping}
        chartRef={chartRef}
        className={classNames(BaseBarChart.className, className)}
        data={propsData}
        id={id}
        isBarSelected={isBarSelected}
        labelDecorator={labelDecorator}
        onBarClick={onBarClick}
        orientation={orientation}
        others={others}
        size={size}
        sortingMethod={sortingMethod}
        theme={theme}
        tooltipDecorator={tooltipDecorator}
        colorTheme={colorTheme}
        tooltipVariant={tooltipVariant}
        xAxisPlacement={xAxisPlacement}
        xAxisVariant={xAxisVariant}
        yAxisPlacement={yAxisPlacement}
        yAxisVariant={yAxisVariant}
        {...forwardedProps}
        ref={ref}
      />
    );
  } else if (dataType === 'LinearData' || dataType === 'TwoCategoryData') {
    return (
      <RenderedLinearBarChart
        barProps={barProps}
        barRole={barRole}
        chartRef={chartRef}
        className={classNames(BaseBarChart.className, className)}
        data={propsData as LinearData}
        id={id}
        isBarSelected={isBarSelected}
        labelDecorator={labelDecorator}
        legendDecorator={legendDecorator}
        legendProps={legendProps}
        legendVariant={legendVariant}
        onBarClick={onBarClick}
        size={size}
        sortingMethod={sortingMethod}
        theme={theme}
        tooltipDecorator={tooltipDecorator}
        colorTheme={colorTheme}
        tooltipVariant={tooltipVariant}
        xAxisPlacement={xAxisPlacement}
        xAxisVariant={xAxisVariant}
        yAxisPlacement={yAxisPlacement}
        yAxisVariant={yAxisVariant}
        {...forwardedProps}
        ref={ref}
      />
    );
  }

  return null;
});
BaseBarChart.className = CLASSNAME;
BaseBarChart.displayName = COMPONENT_NAME;

export const BarChart = Object.assign(BaseBarChart, {
  Section: BarChartSection,
  Bars: BarChartBars,
  Overlay: BarChartOverlay,
  Axis: Axis,
  GroupedTooltip: BarChartGroupedTooltip,
  Legend: Legend,
  LegendItem: LegendItem,
  Title: ChartContainerTitle,
  Description: ChartContainerDescription,
});
